import type { AppAction } from '@app/state'

import type { ClientConfig, IApiConfig, IClinicConfig } from './interfaces'

export const enum FetchState {
  Pending = 'pending',
  Fetching = 'fetching',
  Fetched = 'fetched',
  Error = 'error',
}

export const enum ErrorState {
  None,
  ClientConfigFetch,
  ApiConfigFetch,
  ClinicConfigFetch,
}

export interface State {
  api: null | IApiConfig
  apiConfigFetchState: null | FetchState
  client: null | ClientConfig
  apiLocation: null | string
  clientConfigFetchState: null | FetchState
  clinic: null | IClinicConfig
  clinicConfigFetchState: null | FetchState
  errorState: ErrorState
}

export const enum ActionType {
  SetApiLocation = 'CONFIG/SET_API_LOCATION',
  RestoreApiLocation = 'CONFIG/RESTORE_API_LOCATION',
  FetchClientConfig = 'CONFIG/FETCH_CLIENT_CONFIG',
  FetchClientConfigStarted = 'CONFIG/FETCH_CLIENT_CONFIG/STARTED',
  FetchClientConfigFailure = 'CONFIG/FETCH_CLIENT_CONFIG/FAILURE',
  FetchClientConfigSuccess = 'CONFIG/FETCH_CLIENT_CONFIG/SUCCESS',
  FetchApiConfig = 'CONFIG/FETCH_API_CONFIG',
  FetchApiConfigStarted = 'CONFIG/FETCH_API_CONFIG/STARTED',
  FetchApiConfigFailure = 'CONFIG/FETCH_API_CONFIG/FAILURE',
  FetchApiConfigSuccess = 'CONFIG/FETCH_API_CONFIG/SUCCESS',
  FetchClinicConfig = 'CONFIG/FETCH_CLINIC_CONFIG',
  FetchClinicConfigStarted = 'CONFIG/FETCH_CLINIC_CONFIG/STARTED',
  FetchClinicConfigFailure = 'CONFIG/FETCH_CLINIC_CONFIG/FAILURE',
  FetchClinicConfigSuccess = 'CONFIG/FETCH_CLINIC_CONFIG/SUCCESS',
}

export type Action =
  | { type: ActionType.SetApiLocation; location: null | string }
  | { type: ActionType.RestoreApiLocation; location: string }
  | { type: ActionType.FetchClientConfig }
  | { type: ActionType.FetchClientConfigStarted }
  | { type: ActionType.FetchClientConfigFailure }
  | { type: ActionType.FetchClientConfigSuccess; config: ClientConfig }
  | { type: ActionType.FetchApiConfig }
  | { type: ActionType.FetchApiConfigStarted }
  | { type: ActionType.FetchApiConfigFailure }
  | { type: ActionType.FetchApiConfigSuccess; config: IApiConfig }
  | { type: ActionType.FetchClinicConfig }
  | { type: ActionType.FetchClinicConfigStarted }
  | { type: ActionType.FetchClinicConfigFailure }
  | { type: ActionType.FetchClinicConfigSuccess; config: IClinicConfig }

export const actions = {
  setApiLocation(location: null | string): Action {
    return { type: ActionType.SetApiLocation, location }
  },
  restoreApiLocation(location: null | string): Action {
    return { type: ActionType.RestoreApiLocation, location }
  },
  fetchClientConfig(): Action {
    return { type: ActionType.FetchClientConfig }
  },
  fetchClientConfigStarted(): Action {
    return { type: ActionType.FetchClientConfigStarted }
  },
  fetchClientConfigFailure(): Action {
    return { type: ActionType.FetchClientConfigFailure }
  },
  fetchClientConfigSuccess(config: ClientConfig): Action {
    return { type: ActionType.FetchClientConfigSuccess, config }
  },
  fetchApiConfig(): Action {
    return { type: ActionType.FetchApiConfig }
  },
  fetchApiConfigStarted(): Action {
    return { type: ActionType.FetchApiConfigStarted }
  },
  fetchApiConfigFailure(): Action {
    return { type: ActionType.FetchApiConfigFailure }
  },
  fetchApiConfigSuccess(config: IApiConfig): Action {
    return { type: ActionType.FetchApiConfigSuccess, config }
  },
  fetchClinicConfig(): Action {
    return { type: ActionType.FetchClinicConfig }
  },
  fetchClinicConfigStarted(): Action {
    return { type: ActionType.FetchClinicConfigStarted }
  },
  fetchClinicConfigFailure(): Action {
    return { type: ActionType.FetchClinicConfigFailure }
  },
  fetchClinicConfigSuccess(config: IClinicConfig): Action {
    return { type: ActionType.FetchClinicConfigSuccess, config }
  },
}

export const initialState: State = {
  api: null,
  apiConfigFetchState: FetchState.Pending,
  client: null,
  apiLocation: null,
  clientConfigFetchState: FetchState.Pending,
  errorState: ErrorState.None,
  clinic: null,
  clinicConfigFetchState: null,
}

export function reducer(state: State = initialState, action: AppAction): State {
  switch (action.type) {
    case ActionType.SetApiLocation:
    case ActionType.RestoreApiLocation:
      return {
        ...state,
        client: {
          ...state.client,
          apiLocation: action.location,
        },
        apiLocation: action.location,
        errorState: ErrorState.None,
      }

    case ActionType.FetchClientConfigStarted:
      return {
        ...state,
        clientConfigFetchState: FetchState.Fetching,
        errorState: ErrorState.None,
      }

    case ActionType.FetchClientConfigFailure:
      return {
        ...state,
        client: null,
        clientConfigFetchState: FetchState.Error,
        errorState: ErrorState.ClientConfigFetch,
      }

    case ActionType.FetchClientConfigSuccess:
      return {
        ...state,
        client: action.config,
        apiLocation: action.config.apiLocation,
        clientConfigFetchState: FetchState.Fetched,
      }
    case ActionType.FetchApiConfigStarted:
      return {
        ...state,
        errorState: ErrorState.None,
        apiConfigFetchState: FetchState.Fetching,
      }

    case ActionType.FetchApiConfigFailure:
      return {
        ...state,
        api: null,
        errorState: ErrorState.ApiConfigFetch,
        apiConfigFetchState: FetchState.Error,
      }

    case ActionType.FetchApiConfigSuccess:
      return {
        ...state,
        api: action.config,
        apiConfigFetchState: FetchState.Fetched,
      }

    case ActionType.FetchClinicConfigStarted:
      return {
        ...state,
        errorState: ErrorState.None,
        clinicConfigFetchState: FetchState.Fetching,
      }

    case ActionType.FetchClinicConfigFailure:
      return {
        ...state,
        clinic: null,
        errorState: ErrorState.ClinicConfigFetch,
        clinicConfigFetchState: FetchState.Error,
      }

    case ActionType.FetchClinicConfigSuccess:
      return {
        ...state,
        clinic: action.config,
        clinicConfigFetchState: FetchState.Fetched,
      }

    default:
      return state
  }
}

export const queries = {
  getApiLocation(state: State): null | string {
    return state.client && state.client.apiLocation
      ? state.client.apiLocation
      : null
  },
  hasApiLocation(state: State): boolean {
    return state.client !== null && state.client.apiLocation !== null
  },
  hasValidApiLocation(state: State): boolean {
    try {
      const parsed = new URL(state.client.apiLocation)
      return ['http:', 'https:'].includes(parsed.protocol)
    } catch {
      return false
    }
  },
  isApiLocationFetching(state: State): boolean {
    return state.clientConfigFetchState === FetchState.Fetching
  },
  isConfigFetching(state: State): boolean {
    return state.apiConfigFetchState === FetchState.Fetching
  },
}
